/**
 * Motion tier — how much decorative motion a device should be asked to do.
 *
 *  - 'full'  : everything, including the few small filter animations
 *  - 'lite'  : compositor-only motion (transform/opacity); no filter tweens
 *  - 'still' : prefers-reduced-motion — plain fades only
 *
 * Every animation in the app must stay at full frame rate, so anything that
 * is not transform/opacity is gated behind 'full', and 'full' is only granted
 * to a fine-pointer device with a reasonable amount of memory and cores.
 */
export type MotionTier = 'full' | 'lite' | 'still';

export interface MotionEnv {
  reducedMotion: boolean;
  coarsePointer: boolean;
  deviceMemoryGB?: number;
  cores?: number;
}

export function motionTierFor(env: MotionEnv): MotionTier {
  if (env.reducedMotion) return 'still';
  if (env.coarsePointer) return 'lite';
  if (env.deviceMemoryGB !== undefined && env.deviceMemoryGB <= 4) return 'lite';
  if (env.cores !== undefined && env.cores <= 4) return 'lite';
  return 'full';
}

export function getMotionTier(): MotionTier {
  if (typeof window === 'undefined') return 'still';
  const nav = navigator as Navigator & { deviceMemory?: number };
  return motionTierFor({
    reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true,
    coarsePointer: window.matchMedia?.('(pointer: coarse)')?.matches === true,
    deviceMemoryGB: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined,
    cores: typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : undefined,
  });
}

/**
 * A soft light blob as a plain radial gradient — visually the same as a
 * blurred disc, but rasterised once and moved by the compositor for free.
 * `hex` must be a 6-digit hex colour.
 */
export function softBlob(hex: string): string {
  return `radial-gradient(circle closest-side, ${hex} 0%, ${hex}D9 20%, ${hex}8C 44%, ${hex}33 68%, transparent 100%)`;
}
